import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image'
import React from 'react'
import { PrismicRichText } from '@prismicio/react'

import {
  PrismicLinkType,
  PrismicStructuredTextType,
} from 'src/typings/generated/graphql'

import * as GlobalStyles from 'src/styles/global.module.scss'

import EyebrowBlue from 'src/images/svgs/EyebrowBlue.svg'
import CustomLink from 'src/utils/customLink'
import { generateRandomId } from 'src/utils/domHelper'
import * as Styles from './recruitingCta.module.scss'

enum RecruitingCtaImageLayout {
  left = 'left',
  right = 'right',
}

export type RecruitingCtaContent = {
  anchor?: string
  headlineImage?: IGatsbyImageData
  headlineImageAlt?: string
  description?: PrismicStructuredTextType
  link?: PrismicLinkType
  linkText?: string
  image?: IGatsbyImageData
  imageAlt?: string
  imageLayout?: string
  backgroundImage?: IGatsbyImageData
  backgroundImageAlt?: string
}

type Props = {
  content: RecruitingCtaContent
}

const RecruitingCta = ({ content }: Props) => {
  const {
    anchor = generateRandomId(),
    headlineImage,
    headlineImageAlt = '',
    description,
    link,
    linkText = '',
    image,
    imageAlt = '',
    imageLayout = RecruitingCtaImageLayout.left,
    backgroundImage,
    backgroundImageAlt = '',
  } = content

  const imageLayoutFormatted = imageLayout.toLowerCase()
  let layoutClass = ''
  if (imageLayoutFormatted === RecruitingCtaImageLayout.right) {
    layoutClass = Styles.imageRight
  }

  const renderEyebrow = () => (
    <div className={Styles.eyebrow}>
      <EyebrowBlue />
    </div>
  )

  const renderHeadline = () => {
    if (!headlineImage) return null

    return (
      <div className={Styles.headlineImage}>
        <GatsbyImage
          image={headlineImage}
          alt={headlineImageAlt}
          className={Styles.headlineImg}
        />
      </div>
    )
  }

  const renderLink = () => {
    if (!link?.url) return null

    const tempLink = link as PrismicLinkType

    return (
      <CustomLink
        link={tempLink}
        className={GlobalStyles.whiteGhostButtonResponsive}
      >
        {linkText ?? ''}
      </CustomLink>
    )
  }

  return (
    <section
      id={anchor}
      className={`${Styles.recruitingCtaSlice} ${layoutClass}`}
    >
      {!!backgroundImage && (
        <div className={Styles.backgroundWrapper}>
          <GatsbyImage
            image={backgroundImage}
            alt={backgroundImageAlt}
            className={Styles.backgroundImage}
          />
        </div>
      )}
      <div className={Styles.container}>
        {!!image && (
          <div className={Styles.imageWrapper}>
            <GatsbyImage
              image={image}
              alt={imageAlt ?? ''}
              className={Styles.image}
            />
          </div>
        )}
        <div className={Styles.contentWrapper}>
          <div className={Styles.content}>
            {renderEyebrow()}
            {renderHeadline()}
            <PrismicRichText
              field={description?.richText}
              components={{
                hyperlink: ({ node, children }: any) =>
                  CustomLink({ link: node.data, children }),
              }}
            />
            <div className={Styles.link}>{renderLink()}</div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default RecruitingCta
